// SPDX-FileCopyrightText: Copyright Orangebot, Inc. and Medplum contributors
// SPDX-License-Identifier: Apache-2.0
import { Button, Group, NumberInput, Radio, Stack, Table, Text } from '@mantine/core';
import type { QuestionnaireResponse, QuestionnaireResponseItem } from '@medplum/fhirtypes';
import type { JSX } from 'react';
import { useState } from 'react';

const ESS = [
  ['ess-reading', 'Sitting and reading'],
  ['ess-television', 'Watching television'],
  ['ess-inactive', 'Sitting inactive in a public place'],
  ['ess-passenger', 'Passenger in a car for an hour'],
  ['ess-lying-down', 'Lying down to rest'],
  ['ess-talking', 'Sitting and talking'],
  ['ess-after-meal', 'Sitting quietly after a meal'],
  ['ess-traffic', 'Stopped in a car at a light'],
] as const;
const ESS_OPTIONS = [
  ['0', 'Would never nod off'],
  ['1', 'Slight chance of nodding off'],
  ['2', 'Moderate chance of nodding off'],
  ['3', 'High chance of nodding off'],
] as const;
const STOP_BANG = [
  ['snore-loudly', 'Snore loudly'],
  ['tired-fatigued', 'Often tired, fatigued, or sleepy'],
  ['stop-breathing', 'Observed to stop breathing during sleep'],
  ['high-blood-pressure', 'Have or treated for high blood pressure'],
  ['age-50-or-older', 'Age 50 or older'],
  ['neck-collar-size', 'Large neck circumference for your sex'],
] as const;

function makeResponse(items: QuestionnaireResponseItem[]): QuestionnaireResponse {
  return { resourceType: 'QuestionnaireResponse', status: 'completed', item: items };
}

interface Props {
  submitting: boolean;
  onSubmit: (epworth: QuestionnaireResponse, stopBang: QuestionnaireResponse) => void;
}

export function SleepHistoryIntakeForm({ submitting, onSubmit }: Props): JSX.Element {
  const [ess, setEss] = useState<Record<string, string>>({});
  const [stopBang, setStopBang] = useState<Record<string, string>>({});
  const [heightFt, setHeightFt] = useState<number | string>('');
  const [heightIn, setHeightIn] = useState<number | string>('');
  const [weight, setWeight] = useState<number | string>('');
  const complete = ESS.every(([id]) => ess[id]) && STOP_BANG.every(([id]) => stopBang[id]);
  const hasMeasurements = heightFt !== '' && heightIn !== '' && weight !== '';

  return (
    <Stack gap="lg">
      <Text fw={600}>Epworth Sleepiness Scale</Text>
      <Text size="xs" c="dimmed">
        Select the response that best describes the chance of nodding off in each situation.
      </Text>
      <Table.ScrollContainer minWidth={640}>
        <Table withTableBorder withColumnBorders verticalSpacing="xs">
          <Table.Thead>
            <Table.Tr>
              <Table.Th />
              {ESS_OPTIONS.map(([value, description]) => (
                <Table.Th key={value} ta="center" style={{ width: '16%', whiteSpace: 'normal' }}>
                  <Text size="xs" fw={500} lh={1.2}>
                    {description}
                  </Text>
                  <Text size="xs" fw={700} mt={2}>
                    {value}
                  </Text>
                </Table.Th>
              ))}
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {ESS.map(([id, label]) => (
              <Table.Tr key={id}>
                <Table.Th scope="row" fw={600} style={{ minWidth: 190 }}>
                  {label}
                </Table.Th>
                {ESS_OPTIONS.map(([value, description]) => (
                  <Table.Td key={value} ta="center">
                    <Radio
                      name={id}
                      value={value}
                      checked={ess[id] === value}
                      onChange={() => setEss({ ...ess, [id]: value })}
                      aria-label={`${value}: ${description}`}
                    />
                  </Table.Td>
                ))}
              </Table.Tr>
            ))}
          </Table.Tbody>
        </Table>
      </Table.ScrollContainer>
      <Text fw={600} mt="md">
        STOP-BANG
      </Text>
      {STOP_BANG.map(([id, label]) => (
        <Radio.Group
          key={id}
          label={label}
          value={stopBang[id] ?? ''}
          onChange={(value) => setStopBang({ ...stopBang, [id]: value })}
        >
          <Group mt="xs">
            <Radio value="Yes" label="Yes" />
            <Radio value="No" label="No" />
          </Group>
        </Radio.Group>
      ))}
      <Group>
        <NumberInput label="Height (ft)" value={heightFt} onChange={setHeightFt} min={3} max={8} />
        <NumberInput label="Height (in)" value={heightIn} onChange={setHeightIn} min={0} max={11} />
        <NumberInput label="Weight (lb)" value={weight} onChange={setWeight} min={1} />
      </Group>
      <Group justify="flex-end">
        <Button
          loading={submitting}
          disabled={!complete || !hasMeasurements || submitting}
          onClick={() => {
            const essItems = ESS.map(([linkId, text]) => ({
              linkId,
              text,
              answer: [{ valueCoding: { code: ess[linkId] } }],
            }));
            const stopItems = [
              ...STOP_BANG.map(([linkId, text]) => ({ linkId, text, answer: [{ valueString: stopBang[linkId] }] })),
              { linkId: 'height-ft', answer: [{ valueInteger: Number(heightFt) }] },
              { linkId: 'height-in', answer: [{ valueInteger: Number(heightIn) }] },
              { linkId: 'weight', answer: [{ valueDecimal: Number(weight) }] },
            ];
            onSubmit(makeResponse(essItems), makeResponse(stopItems));
          }}
        >
          Save intake
        </Button>
      </Group>
    </Stack>
  );
}
